import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import dayjs from 'src/app/config/dayjs.config';
import { EndStandard, Ticket } from 'src/app/model/product';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor() {}

  private readonly PER_PERSON_FIELD_NAMES = ['minParticipants', 'maxParticipants', 'pricePerPerson'];
  private readonly PER_TEAM_FIELD_NAMES = [
    'maxTeamCapacity',
    'minParticipantsPerTeam',
    'maxParticipantsPerTeam',
    'pricePerTeam',
    'extraPricePerPerson',
  ];

  minLengthArray = (min: number) => {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.value.length >= min) return null;
      return { MinLengthArray: true };
    };
  };

  maxLengthArray = (max: number) => {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.value.length <= max) return null;
      return { MinLengthArray: true };
    };
  };

  /** 빈칸은 quantity 의 required 가 이미 막으므로, 0 으로 뭉뚱그려 중복으로 오인하지 않는다. */
  private hasDuplicateQuantity(rows: { quantity: unknown }[] | undefined): boolean {
    const quantities = (rows ?? [])
      .filter((row) => row?.quantity !== null && row?.quantity !== undefined && row?.quantity !== '')
      .map((row) => Number(row.quantity));
    return new Set(quantities).size !== quantities.length;
  }

  /** prices FormArray 는 페이지마다 따로 만들어서, 배열마다 붙이면 새 경로가 생길 때 빠진다. 루트 폼 하나에 붙인다. */
  readonly uniqueQuantityForm: ValidatorFn = (form: AbstractControl): { [key: string]: any } | null => {
    const value = (form.value ?? {}) as { prices?: { quantity: unknown }[] };
    return this.hasDuplicateQuantity(value.prices) ? { duplicateQuantity: { root: true } } : null;
  };

  /** photos 배열에서 photo 값이 있는 항목만 FormGroup으로 변환한다 (LLM 응답/빈 슬롯의 null 방어). */
  buildPhotoGroups(photos: Array<{ photo?: string } | null | undefined> | null | undefined): UntypedFormGroup[] {
    return (photos ?? [])
      .filter((p): p is { photo: string } => !!p?.photo)
      .map((p) => new UntypedFormGroup({ photo: new UntypedFormControl(p.photo) }));
  }

  /**
   * 복제 추가 화면에서 원본 그룹의 판매 기간을 폼에 채울 때 오늘 이후로 보정한다.
   *
   * 기간 입력(bsDaterangepicker)에는 `[minDate]="오늘"` 이 걸려 있고, ngx-bootstrap 의
   * `BsDaterangepickerInputDirective.validate()` 는 시작일이 minDate 보다 이르면 에러를 낸다.
   * 그런데 이때 값을 minDate 로 클램프해 **입력창 표시만** 바꾸고(writeValue) FormControl 값은
   * 그대로 두기 때문에, 화면엔 정상 날짜가 보이는데 form.invalid 가 풀리지 않아
   * 사용자가 날짜를 직접 다시 고르기 전까지 저장 버튼이 영구 비활성된다. 게다가 그 에러는
   * 배열로 반환돼 form-errors 의 `errors[key]` 조회에도 걸리지 않아 안내조차 뜨지 않는다.
   * 어제 만든 그룹을 오늘 복제하면 바로 재현된다.
   *
   * 시작일은 오늘로 당기고(= 화면에 이미 보이던 값), 종료일이 그보다 앞서면 시작일에 맞춘다
   * (완전히 과거인 그룹을 복제할 때 종료일 < 시작일 이 되어 티켓이 0개 생성되는 것을 막는다).
   */
  buildCopyPeriod(from: Date | string, to: Date | string): Date[] {
    const today = dayjs().startOf('day');
    const start = dayjs(from).startOf('day');
    const end = dayjs(to).startOf('day');
    const clampedStart = start.isBefore(today) ? today : start;
    const clampedEnd = end.isBefore(clampedStart) ? clampedStart : end;
    return [clampedStart.toDate(), clampedEnd.toDate()];
  }

  getValidators(maxLength: number, validators?: ValidatorFn[]): Validators {
    const basic: ValidatorFn[] = [Validators.required, Validators.maxLength(maxLength)];
    if (!!validators) {
      return Validators.compose(basic.concat(validators));
    }
    return basic;
  }

  mustMatch(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const control = formGroup.get(controlName);
      const matchingControl = formGroup.get(matchingControlName);

      if (!control || !matchingControl) {
        return null;
      }

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        return null;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
      return null;
    };
  }

  /**
   * 최소 인원이 같은 그룹의 최대 인원을 초과하면 isUpper 에러.
   * `control.parent` 로 그룹-로컬 조회를 해야 옵션 폼에서도 자기 그룹의 maxParticipants 와 비교한다
   * (root 로 보면 옵션의 형제가 아니라 최상위 폼을 봐서 옵션 검증이 무력화됨).
   * 컨트롤 생성 시점엔 아직 그룹에 붙기 전이라 parent 가 null 일 수 있으므로 옵셔널 체이닝으로 접근하고,
   * 두 값이 모두 입력되기 전(null)에는 비교하지 않는다(빈 기본값 null 에서의 런타임 에러 방지).
   */
  validationMinControl(controlName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const max = control.parent?.value?.[controlName];
      const min = control.value;
      if (max == null || min == null) {
        return null;
      }
      return min > max ? { isUpper: true } : null;
    };
  }

  /**
   * 인원별(PER_PERSON) 인원/가격 컨트롤을 group에 추가한다.
   * 기본값은 빈칸(null) + required 로, 호스트가 값을 명시적으로 입력하게 강제한다.
   * 가격(pricePerPerson)은 min 을 두지 않아 0원(무료 모임)을 허용한다.
   * 팀별 컨트롤이 남아 있으면 제거해 단위 전환 시 잔존 컨트롤이 없도록 한다.
   */
  addPerPersonControls(group: UntypedFormGroup): void {
    group.addControl(
      'minParticipants',
      new UntypedFormControl(
        null,
        Validators.compose([Validators.required, Validators.min(1), this.validationMinControl('maxParticipants')]),
      ),
    );
    group.addControl(
      'maxParticipants',
      new UntypedFormControl(null, Validators.compose([Validators.required, Validators.max(100)])),
    );
    group.addControl(
      'pricePerPerson',
      new UntypedFormControl(null, Validators.compose([Validators.required, Validators.max(10000000)])),
    );
    this.PER_TEAM_FIELD_NAMES.forEach((name) => group.removeControl(name));
  }

  /**
   * 팀별(PER_TEAM) 인원/가격 컨트롤을 group에 추가한다.
   * 기본값은 빈칸(null) + required 로 미입력을 차단한다.
   * 정원(maxTeamCapacity)·팀 인원·1팀 시작가(pricePerTeam)는 min(1) 로 0/빈값을 차단하고,
   * 1인 추가 금액(extraPricePerPerson)은 0원(추가금 없음)을 허용한다.
   * 인원별 컨트롤이 남아 있으면 제거한다.
   */
  addPerTeamControls(group: UntypedFormGroup): void {
    group.addControl(
      'maxTeamCapacity',
      new UntypedFormControl(null, Validators.compose([Validators.required, Validators.min(1)])),
    );
    group.addControl(
      'minParticipantsPerTeam',
      new UntypedFormControl(null, Validators.compose([Validators.required, Validators.min(1)])),
    );
    group.addControl(
      'maxParticipantsPerTeam',
      new UntypedFormControl(null, Validators.compose([Validators.required, Validators.min(1)])),
    );
    group.addControl(
      'pricePerTeam',
      new UntypedFormControl(null, Validators.compose([Validators.required, Validators.min(1)])),
    );
    group.addControl('extraPricePerPerson', new UntypedFormControl(null, Validators.required));
    this.PER_PERSON_FIELD_NAMES.forEach((name) => group.removeControl(name));
  }

  /**
   * 기존 티켓을 폼에 싣는 화면(편집·복제 추가 등)에서 부모 티켓의 인원/가격 폼 컨트롤을
   * (재)구성한다.
   * 스케줄 편집 페이지는 라우트 파라미터(:id)만 바뀌어도 폼을 다시 만들지 않고 같은 컴포넌트
   * 인스턴스를 재사용하므로, 이전에 연 티켓의 컨트롤이 남지 않도록 인원/가격 컨트롤을 먼저
   * 모두 제거한다(복제 추가 화면은 setForm() 으로 폼을 새로 만들어 오므로 이 제거는 no-op).
   */
  setParentPricingControls(form: UntypedFormGroup, ticket: Ticket): void {
    [...this.PER_PERSON_FIELD_NAMES, ...this.PER_TEAM_FIELD_NAMES].forEach((name) => form.removeControl(name));

    if (ticket.endStandard === EndStandard.PER_PERSON) {
      this.addPerPersonControls(form);
      form.controls.minParticipants.setValue(ticket.minParticipants);
      form.controls.maxParticipants.setValue(ticket.maxParticipants);
      form.controls.pricePerPerson.setValue(ticket.pricePerPerson);
    } else {
      this.addPerTeamControls(form);
      form.controls.maxTeamCapacity.setValue(ticket.maxTeamCapacity);
      form.controls.minParticipantsPerTeam.setValue(ticket.minParticipantsPerTeam);
      form.controls.maxParticipantsPerTeam.setValue(ticket.maxParticipantsPerTeam);
      form.controls.pricePerTeam.setValue(ticket.pricePerTeam);
      form.controls.extraPricePerPerson.setValue(ticket.extraPricePerPerson);
    }
  }

  getInvalidControls(form: UntypedFormGroup): string[] {
    const invalids = [];
    const controls = form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalids.push(name);
      }
    }
    return invalids;
  }

  autoSave(values: any) {
    localStorage.setItem('product-add', JSON.stringify(values));
  }

  loadAutoSave(key: string) {
    const value = localStorage.getItem(key);
    if (!value) return undefined;
    return JSON.parse(value);
  }

  removeAuthSavedValue(key: string) {
    localStorage.removeItem(key);
  }
}
